import { Box, Tab, Tabs, useTheme } from '@mui/material'
import { useUpdateTaskChecklist } from 'api/aws/checklists/useUpdateTaskChecklist'
import { useEntitiesFromSearch } from 'api/search'
import React, { useEffect, useMemo, useState } from 'react'
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom'
import { TabItem, User } from 'types'
import CustomLoadingOverlay from 'components/DataGrid/CustomLoadingOverlay'
import GoogleDocTab from './GoogleDocTab'
import { item } from './itemsTest'
import JotformTab from './JotformTab'
import TabIcon from './TabIcon'
import { TabLabel } from './TabLabel'
import './Tabs.css'
import { ACTIONS } from './utils'

const Panel = ({ children, active }: { children: React.ReactNode; active: boolean }) => {
  // keep mounted but visually hide to avoid unmount/remount jitter
  return (
    <Box
      role="tabpanel"
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        display: active ? 'flex' : 'none',
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  )
}

type TeamDetailsTypes = {
  AM_USERS: User[]
  DMS_USERS: User[]
  SEO_USERS: User[]
  INTEGRATION_USERS: User[]
}

type TabRoutingProps = {
  config: TabItem[]
  onSaveAll?: () => void
  seoTier?: number
  team_details?: TeamDetailsTypes
  count?: number | null
  isNavigationDisabled?: boolean
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  }
}

const TabRouting: React.FC<TabRoutingProps> = ({
  config,
  onSaveAll,
  seoTier,
  team_details,
  count,
  isNavigationDisabled,
}) => {
  const theme = useTheme()
  const { PK, SK } = useParams() as { PK?: string; SK?: string }
  const navigate = useNavigate()
  const location: Location = useLocation()

  // Force re-render when pathname changes - needed because some child components
  // (like RichTextField/Tiptap editors) can block React's update propagation
  const [, forceUpdate] = useState(0)
  useEffect(() => {
    forceUpdate(n => n + 1)
  }, [location.pathname])

  const [isEditMode, setIsEditMode] = useState(false)
  const [editingChecklist, setEditingChecklist] = useState(false)
  const [draftChecklist, setDraftChecklist] = useState<Array<any> | []>([])
  const { mutate: updateChecklist } = useUpdateTaskChecklist()
  const TAB_SEGMENTS = useMemo(() => config?.map(c => c.label) ?? [], [config])

  const { organizations } = useEntitiesFromSearch()
  const organization = useMemo(() => {
    if (!organizations) return null
    return organizations?.find(o => o?.PK === PK) ?? null
  }, [organizations, PK])

  const onStartEdit = () => {
    setIsEditMode(true)
  }

  const onStopEdit = () => {
    setIsEditMode(false)
  }

  // Derive the current tab index from URL pathname
  const computeCurrentIndex = () => {
    if (!TAB_SEGMENTS.length) return 0

    // Parse tab from pathname: /mtb/:PK/:tab or /nco/:PK/:SK/:tab
    const parts = location.pathname.split('/').filter(Boolean)
    const base = parts[0]
    const isMtb = base === 'mtb'
    const urlTab = isMtb ? parts[2] : parts[3]

    const seg = urlTab ?? TAB_SEGMENTS[0]
    const idx = TAB_SEGMENTS.indexOf(seg)
    return idx === -1 ? 0 : idx
  }

  const currentIndex = computeCurrentIndex()
  const active = config[currentIndex]

  // Whether this tab supports editing
  const canEditThisTab = useMemo(() => {
    if (!active) return false
    const { needsEditing, props } = active
    return typeof needsEditing === 'function' ? !!needsEditing(props) : !!needsEditing
  }, [active])

  useEffect(() => {
    if (!TAB_SEGMENTS.length) return
    const parts = location.pathname.split('/').filter(Boolean)
    const base = parts[0]
    const isMtb = base === 'mtb'

    // For MTB routes: /mtb/:PK/:tab
    if (isMtb) {
      const urlPK = parts[1]
      const urlTab = parts[2]

      // Don't navigate if we're at base /mtb (drawer is closing/closed)
      if (!urlPK) return

      // Only auto-navigate to default tab if PK exists but tab is missing/invalid
      if (!urlTab || TAB_SEGMENTS.indexOf(urlTab) === -1) {
        navigate(`/${base}/${urlPK}/${TAB_SEGMENTS[0]}`, { replace: true })
      }
      return
    }

    // For non-MTB routes (require SK): /nco/:PK/:SK/:tab or /teamtasks/:PK/:SK/:tab
    const urlPK = parts[1]
    const urlSK = parts[2]
    const urlTab = parts[3]

    if (!urlPK || !urlSK) return
    if (!urlTab || TAB_SEGMENTS.indexOf(urlTab) === -1) {
      navigate(`/${base}/${urlPK}/${urlSK}/${TAB_SEGMENTS[0]}`, { replace: true })
    }
  }, [TAB_SEGMENTS, navigate, location.pathname])

  const handleChange = (_e: React.SyntheticEvent, newIndex: number) => {
    if (typeof newIndex === 'string') return
    const base = location.pathname.split('/')[1]
    const seg = TAB_SEGMENTS[newIndex]
    if (!PK || !seg) return

    const route = base === 'mtb' ? `/${base}/${PK}/${seg}` : SK ? `/${base}/${PK}/${SK}/${seg}` : undefined

    if (route) navigate(route, { replace: true })
  }

  /* ---- Checklist Save ---- */
  const onSaveChecklist = () => {
    const nextProgress = {
      total: draftChecklist.length,
      //@ts-ignore
      done: draftChecklist.filter(i => i?.done).length,
    }

    updateChecklist(
      {
        PK: item.PK,
        SK: item.SK,
        checklist: draftChecklist,
        progress: nextProgress,
      },
      {
        onSuccess: () => {
          // Checklist saved successfully
        },
      }
    )
    setEditingChecklist(false)
  }

  const onSave = useMemo(() => {
    const kind = active?.saveKind ?? 'none' // 'all' | 'checklist' | 'none'
    if (kind === 'checklist') return onSaveChecklist
    if (kind === 'all') return onSaveAll
    return undefined
  }, [active?.saveKind, onSaveAll, onSaveChecklist])

  if (!TAB_SEGMENTS.length) return <CustomLoadingOverlay bgColor="transparent" />

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', height: '100%', width: '100%', minHeight: 0 }}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={currentIndex}
        onChange={handleChange}
        aria-label="Vertical tabs example"
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          '& .MuiTabs-scroller': {
            paddingTop: 0,
            paddingBottom: 0,
          },
          '& .MuiTab-root': {
            minHeight: 32,
            py: 2,
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          },
          '& .MuiTab-labelIcon': {
            minHeight: 32,
          },
          '& .MuiTab-wrapper': {
            flexDirection: 'row',
            gap: 0.5,
            lineHeight: 1.2,
            alignItems: 'center',
          },
          '--selected-tab-background': theme.palette.background.default,
        }}
      >
        {TAB_SEGMENTS.map((seg, i) => {
          const isCurrentTab = i === currentIndex
          return (
            <Tab
              key={`${seg}-${i}`}
              disabled={isNavigationDisabled && !isCurrentTab}
              icon={
                <TabIcon
                  title={seg}
                  team_details={
                    seg.includes('dms')
                      ? team_details?.DMS_USERS
                      : seg.includes('am')
                      ? team_details?.AM_USERS
                      : seg.includes('seo')
                      ? team_details?.SEO_USERS
                      : seg.includes('integration')
                      ? team_details?.INTEGRATION_USERS
                      : []
                  }
                  count={count}
                />
              }
              label={<TabLabel label={seg} />}
              disableRipple
              {...a11yProps(i)}
              sx={{
                opacity: isNavigationDisabled && !isCurrentTab ? 0.5 : 1,
                cursor: isNavigationDisabled && !isCurrentTab ? 'not-allowed' : 'pointer',
              }}
            />
          )
        })}
        {seoTier && seoTier > 0 && <JotformTab seoTier={seoTier} />}
        {organization?.handoffDocId && <GoogleDocTab handoffDocId={organization?.handoffDocId} />}

        {/* Separator */}
        <Tab
          value="action-separator"
          disabled
          disableRipple
          label=""
          onMouseDown={e => e.preventDefault()}
          sx={{
            minHeight: 8,
            p: 0,
            cursor: 'default',
            '&.Mui-disabled': { opacity: 1 },
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            mx: 0.5,
          }}
        />

        {/* Edit Button */}
        {canEditThisTab && !isEditMode && (
          <Tab
            value={ACTIONS.edit}
            icon={<TabIcon title="Edit" />}
            label={<TabLabel label="Edit" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onStartEdit()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0 } }}
          />
        )}

        {/* Cancel Button */}
        {canEditThisTab && isEditMode && (
          <Tab
            value={ACTIONS.cancel}
            icon={<TabIcon title="Cancel" />}
            label={<TabLabel label="Cancel" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onStopEdit()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0 } }}
          />
        )}

        {/* Save Button (non-checklist tabs) */}
        {canEditThisTab && isEditMode && onSave && (
          <Tab
            value={ACTIONS.save}
            icon={<TabIcon title="Save" />}
            label={<TabLabel label="Save" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onSave?.()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0 } }}
          />
        )}

        {/* Save Checklist Button */}
        {canEditThisTab && isEditMode && (
          <Tab
            value={ACTIONS.saveChk}
            icon={<TabIcon title="Save Chklist" />}
            label={<TabLabel label="Save" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onSaveChecklist()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0 } }}
          />
        )}
      </Tabs>

      {config.map((item, i) => {
        // Only render active tab - lazy load for performance
        if (currentIndex !== i) return null

        const childProps = { ...item.props, editing: isEditMode }

        return (
          <React.Fragment key={`${item.label}-${PK}-${i}`}>
            <Panel active={true}>{React.createElement(item.Component, childProps)}</Panel>
          </React.Fragment>
        )
      })}
    </Box>
  )
}

export default TabRouting
